import { safeFetch } from "@/api/safeFetch";
import { PopupBaja } from "@/components/popup";
import { ButtonSiguiente, Input } from "@/components/ui";
import { ProfesionalBajaProps } from "@/types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const BajaProfesionales: React.FC = () => {
    const [busqueda, setBusqueda] = useState("");
    const [profesional, setProfesional] = useState<ProfesionalBajaProps | null>(null);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [, setLoading] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [, setShowSuccessPopup] = useState(false);
    const token = localStorage.getItem("token") ?? "";
    const navigate = useNavigate();

    const handleBuscar = async () => {
        if (!busqueda.trim()) {
          setError("Introduce DNI / NIE / CIF");
          return;
        }
      
        const controller = new AbortController();
        const signal = controller.signal;
      
        setError("");
        setSuccess("");
        setProfesional(null);
      
        try {
          const res = await safeFetch(
            `/api/profesionales/buscar?q=${encodeURIComponent(busqueda)}`,
            { headers: { Authorization: `Bearer ${token}` }, signal }
          );
      
          if (!res.ok) {
            setError("Profesional no encontrado");
            return;
          }
      
          const data = await res.json();
          const item = Array.isArray(data) ? data[0] : data;
      
          setProfesional({
            profesional_id: item.profesional_id,
            nombre: item.nombre ?? "",
            apellidos: item.apellidos ?? "",
            email: item.email ?? "",
            dni: item.dni ?? null,
            nie: item.nie ?? null,
            empresa: {
              nombre_empresa: item.nombre_empresa ?? "",
              cif: item.cif ?? "",
              correo_perfil: item.correo_perfil ?? "",
            },
            perfiles: item.perfiles?.map((p: any) => ({
              perfil_profesional_id: p.perfil_profesional_id,
              descripcion: p.descripcion ?? "",
              foto_perfil: p.foto_perfil ?? "",
              direccion: {
                calle: p.calle ?? "",
                localidad: p.localidad ?? "",
                provincia: p.provincia ?? "",
                codigo_postal: p.codigo_postal ?? "",
                pais: p.pais ?? "",
              },
            })) ?? [],
          });
        } catch (err: any) {
          if (err.name !== "AbortError") {
            setError("Error de conexión");
          }
        }
      
        return () => controller.abort();
      };
      


    const confirmarBaja = async () => {
        if (!profesional) return;

        setLoading(true);
        setError("");

        try {
            const res = await safeFetch(
                `/api/profesionales/${profesional.profesional_id}/baja`,
                {
                    method: "PUT",
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (!res.ok) throw new Error("No se pudo dar de baja");

            setShowConfirm(false);
            setShowSuccessPopup(true); // 👈 aquí
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        return () => {
          setBusqueda("");
          setProfesional(null);
          setError("");
          setSuccess("");
          setLoading(false);
          setShowConfirm(false);
          setShowSuccessPopup(false);
        };
      }, []);
      


    return (
        <div className="w-full h-screen flex items-center justify-center relative bg-zinc-500 p-4">
             
              {/* 🔙 Botón volver arriba izquierda */}
            <div className="absolute top-4 left-4">
                <ButtonSiguiente
                    onClick={() => navigate("/backoffice")}
                    className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition shadow"
                >
                    ⬅ Volver
                </ButtonSiguiente>
            </div>

            <div className="w-full max-w-3xl bg-zinc-400 p-6 rounded-xl shadow-md border-zinc-600 border-r-4 border-t-2 flex flex-col gap-6 shadow-blue-700 h-[calc(90vh-4rem)]">
                <h2 className="text-2xl text-center font-bold">Baja de Profesional</h2>

                <div className="w-full bg-blue-600 p-4 rounded-xl flex gap-4 items-center border-zinc-200 border-r-4 border-t-2 border shadow-lg shadow-blue-700">
                    <Input
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        placeholder="Introduce DNI / NIE / CIF"
                        className="flex-1 w-1/2"
                    />
                    <ButtonSiguiente className="flex-1 w-1/2" onClick={handleBuscar}>Buscar</ButtonSiguiente>
                </div>

                {error && <p className="text-red-600">{error}</p>}
                {success && <p className="text-green-600">{success}</p>}

                {profesional && (
                    <div className="bg-zinc-300 p-4 rounded-lg border shadow space-y-2">
                        <p><b>Nombre:</b> {profesional.nombre} {profesional.apellidos}</p>
                        {profesional.dni && <p><b>DNI:</b> {profesional.dni}</p>}
                        {profesional.nie && <p><b>NIE:</b> {profesional.nie}</p>}
                        <p><b>Email:</b> {profesional.email}</p>
                        {profesional.empresa?.nombre_empresa && <p><b>Empresa:</b> {profesional.empresa.nombre_empresa}</p>}
                        {profesional.empresa?.cif && <p><b>CIF:</b> {profesional.empresa.cif}</p>}
                        {profesional.empresa?.correo_perfil && <p><b>Email Empresa:</b> {profesional.empresa.correo_perfil}</p>}

                        <ButtonSiguiente
                            className="mt-4 bg-red-600 hover:bg-red-700 text-white w-full"
                            onClick={() => setShowConfirm(true)}
                        >
                            Dar de baja profesional
                        </ButtonSiguiente>
                    </div>
                )}


            </div>

            {showConfirm && (
                <PopupBaja
                    onCancel={() => setShowConfirm(false)}
                    onConfirm={confirmarBaja}
                />
            )}

        </div>
    );
};

export default BajaProfesionales